<script lang="ts" setup>
import { ref, reactive, computed, onMounted, nextTick } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { email, required, minLength } from '@vuelidate/validators'
import { Spin } from 'ant-design-vue'
import BsButton from '@/legacy/components/Button.vue'
import BsDialogFull from '@/legacy/components/DialogFull.vue'
import BsTextFieldOutline from '@/legacy/components/TextFieldOutline.vue'
import { createCustomEvent, createGAEvent } from '@/assets/ts/customEvent'
import loginHelper from '@/assets/ts/loginHelper'
import useHashQuery from '@/legacy/ts/useHashQuery'
import buttonGoogle from '@/assets/images/button_google@2x.png'
import buttonFb from '@/assets/images/button_fb@2x.png'
import { instanceBuyandship } from '@/assets/ts/apiManagement'
import { getSiteBaseUrl } from '@/lib/siteBaseUrl'

const props = defineProps({
	text: {
		type: Object,
	},
	isSupportEmailLogin: {
		type: Boolean,
		default: false,
	},
	isSupportFacebookLogin: {
		type: Boolean,
		default: false,
	},
	isSupportGoogleLogin: {
		type: Boolean,
		default: false,
	},
})

const state = reactive({
	firstName: '',
	lastName: '',
	email: '',
	password: '',
})

const isShowPanel = ref(true)
const isLoading = ref(false)
const step = ref(1)
const errorMessage = ref('')
const isShowPassword = ref(false)

const rules = {
	firstName: {
		required,
	},
	lastName: {
		required,
	},
	email: {
		required,
		email,
	},
	password: {
		required,
		minLength: minLength(6),
	},
}

const $v = useVuelidate(rules, state)
const { searchParameter, hashParameter } = useHashQuery()

const firstNameValidMsg = computed(() => {
	let msg = ''
	if ($v.value.firstName.$dirty && $v.value.firstName.required.$invalid) {
		msg = props.text?.firstName.required
	}
	return msg
})

const lastNameValidMsg = computed(() => {
	let msg = ''
	if ($v.value.lastName.$dirty && $v.value.lastName.required.$invalid) {
		msg = props.text?.lastName.required
	}
	return msg
})

const emailValidMsg = computed(() => {
	let msg = ''
	if ($v.value.email.$dirty) {
		if ($v.value.email.required.$invalid) {
			msg = props.text?.email.required
		} else if ($v.value.email.email.$invalid) {
			msg = props.text?.email.invalid
		}
	}
	return msg
})

const passwordValidMsg = computed(() => {
	let msg = ''
	if ($v.value.password.$dirty) {
		if ($v.value.password.required.$invalid) {
			msg = props.text?.password.required
		} else if ($v.value.password.minLength.$invalid) {
			msg = props.text?.password.min
		}
	}
	return msg
})

const passswordInputType = computed(() => {
	return isShowPassword.value ? 'text' : 'password'
})

const loginStatus = computed(() => {
	const status = searchParameter.value.find(query => query.key === 'status')
	return status === undefined ? '' : status.val
})

const formatHashParameter: any = computed(() => {
	return hashParameter.value.length
		? hashParameter.value[0]
				.substring(1)
				.split('&')
				.map(v => v.split('='))
				.reduce((pre, [key, value]) => ({ ...pre, [key]: value }), {})
		: {}
})

const googleToken = computed(() => {
	return (
		Object.keys(formatHashParameter.value).length &&
		(formatHashParameter.value.access_token || null)
	)
})

const googleIcon = computed(() => {
	if (import.meta.env.DEV) {
		return { backgroundImage: `url(${buttonGoogle.src})` }
	}
	return ''
})

const fbIcon = computed(() => {
	if (import.meta.env.DEV) {
		return { backgroundImage: `url(${buttonFb.src})` }
	}
	return ''
})

function goToLogin() {
	createGAEvent('c_login_f_signup')
	const payload = {
		type: 'show',
		data: 'login',
	}
	window.postMessage(payload, window.location.origin)
}

function hidePanel() {
	const payload = {
		type: 'hide',
		data: 'signup',
	}
	window.postMessage(payload, window.location.origin)
}

function nextStep() {
	nextTick(() => {
		createCustomEvent('member_signed_up_ok', null)
	})
}

function signup() {
	if ($v.value.$invalid) return
	isLoading.value = true
	createGAEvent('c_email_signup_f_signup')
	instanceBuyandship
		.post('users/signup', {
			first_name: state.firstName,
			last_name: state.lastName,
			email: state.email,
			password: state.password,
		})
		.then(() => {
			if (step.value === 1) {
				step.value += 1
			}
			errorMessage.value = ''
			createCustomEvent('member_signed_up', { provider: 'basic' })
		})
		.catch(err => {
			let message = 'System is busy, please try later'
			if (
				err.response &&
				err.response.data &&
				err.response.data.error &&
				err.response.data.error.message
			) {
				message = err.response.data.error.message
			}
			errorMessage.value = message
		})
		.finally(() => (isLoading.value = false))
}

function requestFacebookSDK() {
	createGAEvent('c_facebook_signup_f_signup')
	isLoading.value = true
	loginHelper
		.requestFacebookSDK()
		.then((res: any) => {
			errorMessage.value = ''
			window.BsHeaderVm.login({
				...eventParameterFormat(res),
				provider: 'facebook',
			})
			hidePanel()
		})
		.catch(({ message }: { message: string }) => {
			errorMessage.value = message
		})
		.finally(() => {
			isLoading.value = false
		})
}

function eventParameterFormat(obj: any) {
	return Object.keys(obj).length
		? Object.keys(obj).reduce((acc, key) => {
				// @ts-ignore
				if (key !== 'user_token') acc[key] = obj[key]
				return acc
		  }, {})
		: {}
}

function googleSignIn() {
	createGAEvent('c_google_signup_f_signup')
	loginHelper.googleSignIn()
}

function onGoogleSignIn() {
	if (!googleToken.value) {
		errorMessage.value = 'Can not get user token'
		return
	}
	isLoading.value = true
	loginHelper.clearToken()
	loginHelper
		.requestLogin({ token: googleToken.value, provider: 'google' })
		.then(({ data }: { data: any }) => {
			const responseData = data.data || {}
			if (responseData.new_account && responseData.new_account === 'yes') {
				createCustomEvent('member_signed_up', { provider: 'google' })
			}
			errorMessage.value = ''
			window.BsHeaderVm.login({
				...eventParameterFormat(responseData),
				provider: 'google',
			})
			hidePanel()
		})
		.catch(({ error }: { error: string }) => {
			errorMessage.value = error
		})
		.finally(() => {
			isLoading.value = false
		})
}

function sendEvent(name: string) {
	createGAEvent(name)
}

onMounted(() => {
	if (loginStatus.value === 'google-login-success') onGoogleSignIn()
})
</script>

<template>
	<bs-dialog-full v-model="isShowPanel" class="signup" @on-hide="hidePanel">
		<template #content>
			<div class="bs-dialog-full-slider-container">
				<Transition name="dialog-slide">
					<div v-if="step === 1" class="signup-container" key="step1">
						<div class="signup-title text-h5">
							{{ text?.title }}
						</div>
						<div class="signup-subtitle">
							<i class="icon-stars"></i>
							<span>{{ text?.subtitle }}</span>
						</div>
						<template v-if="isSupportEmailLogin">
							<BsTextFieldOutline
								v-model="$v.firstName.$model"
								:placeholder="text?.firstName.placeholder"
								:isError="$v.firstName.$error"
								:errorMessage="firstNameValidMsg"
							></BsTextFieldOutline>
							<BsTextFieldOutline
								v-model="$v.lastName.$model"
								:placeholder="text?.lastName.placeholder"
								:isError="$v.lastName.$error"
								:errorMessage="lastNameValidMsg"
							></BsTextFieldOutline>
							<BsTextFieldOutline
								v-model="$v.email.$model"
								:placeholder="text?.email.placeholder"
								:isError="$v.email.$error"
								:errorMessage="emailValidMsg"
							>
								<template #trailing>
									<i class="icon-mail"></i>
								</template>
							</BsTextFieldOutline>
							<BsTextFieldOutline
								v-model="$v.password.$model"
								:type="passswordInputType"
								:placeholder="text?.password.placeholder"
								:isError="$v.password.$error"
								:errorMessage="passwordValidMsg"
							>
								<template #trailing>
									<i
										:class="[isShowPassword ? 'icon-eye-on' : 'icon-eye-off']"
										@click="isShowPassword = !isShowPassword"
									></i>
								</template>
							</BsTextFieldOutline>
						</template>
						<div class="signup-notice">
							<template v-if="text?.remark.html">
								<span v-html="text?.remark.prefix"></span>
							</template>
							<template v-else>
								<span>{{ text?.remark.prefix }}</span>
								<a
									:href="`${getSiteBaseUrl()}/terms/`"
									@click="sendEvent('c_terms_f_signup')"
								>
									{{ text?.remark.term }}
								</a>
								<span>{{ text?.remark.and }}</span>
								<a
									:href="`${getSiteBaseUrl()}/privacy/`"
									@click="sendEvent('c_privacy_f_signup')"
								>
									{{ text?.remark.policy }}
								</a>
							</template>
						</div>
						<div v-if="isSupportEmailLogin">
							<Spin :spinning="isLoading">
								<bs-button
									class="full-width"
									:class="{ 'bs-button--disabled': $v.$invalid }"
									type="primary"
									solid
									@click="signup"
								>
									{{ text?.button.signup }}
								</bs-button>
							</Spin>
							<div class="signup-action-message" v-show="errorMessage">
								{{ errorMessage }}
							</div>
						</div>
						<div class="signup-separator">
							<span class="signup-separator-center">{{ text?.or }}</span>
						</div>
						<div class="signup-login">
							{{ text?.login.prefix }}
							<a href="javascript:void(0)" @click.prevent="goToLogin">
								{{ text?.login.suffix }}
							</a>
						</div>
						<template v-if="isSupportGoogleLogin">
							<div class="bs-field-group">
								<div
									class="bs-field__button bs-field__button--social"
									@click="googleSignIn"
								>
									<span class="bs-field__button__leading">
										<i
											class="signup-icon singup-icon--google"
											:style="googleIcon"
										></i>
										<span class="bs-field__button__leading__text">
											{{ text?.google.button }}
										</span>
									</span>
									<i class="signup-icon singup-icon--arrowRight"></i>
								</div>
							</div>
						</template>
						<template v-if="isSupportFacebookLogin">
							<div class="bs-field-group">
								<div
									class="bs-field__button bs-field__button--social"
									@click="requestFacebookSDK"
								>
									<span class="bs-field__button__leading">
										<i
											class="signup-icon singup-icon--facebook"
											:style="fbIcon"
										></i>
										<span class="bs-field__button__leading__text">
											{{ text?.facebook.button }}
										</span>
									</span>
									<i class="signup-icon singup-icon--arrowRight"></i>
								</div>
							</div>
						</template>
					</div>
				</Transition>
				<Transition name="dialog-slide">
					<div v-if="step === 2" class="signup-container" key="step2">
						<div class="forgetpassword-step2-media">
							<div class="forgetpassword-step2-media__round">
								<slot name="media"></slot>
							</div>
						</div>
						<div class="forgetpassword-step2-title text-h5">
							{{ text?.check.title }}
						</div>
						<div class="forgetpassword-step2-desc">
							{{ text?.check.subtitle.prefix }}
							<span class="text-subtitle-sm">{{ state.email }}</span>
							.
							<br />
							{{ text?.check.subtitle.suffix }}
						</div>
						<bs-button
							class="full-width"
							type="primary"
							outline
							@click="nextStep"
						>
							{{ text?.button.ok }}
						</bs-button>
					</div>
				</Transition>
			</div>
		</template>
	</bs-dialog-full>
</template>
@/legacy/ts/useHashQuery
